import { MeasurementKind } from '@/engine/app/interfaces.ts';
import type { ViewsService } from '@/engine/app/Views.service.ts';
import type { IMeasurementViewModel, IObjectViewModel } from '@/engine/app/interfaces.ts';
import type { MeasurementBoxInfo, ViewEntity } from '@/engine/app/models/viewEntity/view.entity.ts';
import type { IMeasurementBoxDetails } from '@/surgeries/cardiac/TTVR/components/boxes/measurementBoxes/VariableDistanceMeasurementBox/interfaces.ts';

export function addVariableDistancesViewModels(
  view: ViewEntity,
  viewsService: ViewsService,
  distanceViewModel: IMeasurementViewModel,
  pointViewModel: IObjectViewModel
): void {
  const additionalViews: Array<ViewEntity> = _getAdditionalViewsByBoxKind(
    view,
    viewsService,
    MeasurementKind.VariableDistance,
    (distanceViewModel as IMeasurementBoxDetails).details.boxGroup
  );
  const newPointViewModel: IObjectViewModel = pointViewModel.clone();

  newPointViewModel.setIsTunable(false);
  newPointViewModel.setVisibility(false);
  additionalViews.forEach((view) => {
    view.addObject(newPointViewModel.clone());
    const additionalDistance: IMeasurementViewModel = distanceViewModel.clone() as IMeasurementViewModel;
    additionalDistance.setVisibility(false);
    view.addMeasurement(additionalDistance);
    view.addWorkflowIndication(additionalDistance.descriptor);
  });

  view.addMeasurement(distanceViewModel); // !must be before addObject
  view.addObject(pointViewModel); // !must be after addMeasurement
  view.addMeasurementToBox(distanceViewModel, MeasurementKind.VariableDistance);
  view.addMeasurementVisibilityDependencies(distanceViewModel, pointViewModel);
  view.addWorkflowIndication(distanceViewModel.descriptor);
}

export function removeVariableDistancesViewModels(
  view: ViewEntity,
  viewsService: ViewsService,
  distanceViewModel: IMeasurementViewModel,
  pointViewModel: IObjectViewModel
): void {
  const additionalViews = _getAdditionalViewsByBoxKind(
    view,
    viewsService,
    MeasurementKind.VariableDistance,
    (distanceViewModel as IMeasurementBoxDetails).details.boxGroup
  );
  additionalViews.forEach((view) => {
    view.removeByDescriptor(pointViewModel.descriptor);
    view.removeByDescriptor(distanceViewModel.descriptor);
    view.removeWorkflowIndication(distanceViewModel.descriptor);
  });

  view.removeObject(pointViewModel);
  view.removeMeasurement(distanceViewModel);
  view.removeMeasurementFromBox(distanceViewModel, MeasurementKind.VariableDistance);
  view.removeMeasurementVisibilityDependencies(distanceViewModel, pointViewModel);
  view.removeWorkflowIndication(distanceViewModel.descriptor);
}

function _getAdditionalViewsByBoxKind(
  view: ViewEntity,
  viewsService: ViewsService,
  kind: MeasurementKind,
  boxGroup: string
): Array<ViewEntity> {
  const variableDistanceBox: MeasurementBoxInfo | undefined = view
    .getMeasurementBoxes()
    .filter((box: MeasurementBoxInfo): boolean => box.kind === kind)
    .find((box) => box.group === boxGroup);

  const additionalViewsDescriptors: Array<string> = variableDistanceBox?.details ?? [];

  return additionalViewsDescriptors
    .map((descriptor: string) => viewsService.getViewByDescriptor(descriptor))
    .filter((view: ViewEntity | undefined): boolean => view !== undefined) as Array<ViewEntity>;
}
